import { motion } from "framer-motion";
import { BadgeCheck, Clock, ListChecks, ShieldCheck, MapPin, Egg, Drumstick, Building2, Tractor } from "lucide-react";

const REASONS = [
  {
    icon: BadgeCheck,
    title: "Professionalism",
    desc: "Trained crews, proper equipment, and a professional standard on every job site.",
  },
  {
    icon: Clock,
    title: "Reliability",
    desc: "We work to your production schedule so your barns are ready when you need them.",
  },
  {
    icon: ListChecks,
    title: "Attention to Detail",
    desc: "Thorough cleaning of housing and production areas — corners, lines, and equipment included.",
  },
  {
    icon: ShieldCheck,
    title: "Biosecurity Commitment",
    desc: "Biosecurity-focused cleaning practices that help protect your flock and your operation.",
  },
];

const CUSTOMERS = [
  { icon: Building2, label: "Commercial poultry farmers" },
  { icon: Egg, label: "Broiler chicken producers" },
  { icon: Drumstick, label: "Turkey producers" },
  { icon: Tractor, label: "Farm operators & agricultural businesses" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" } }),
};

export const WhyIMR = () => {
  return (
    <section id="why-imr" data-testid="why-imr-section" className="bg-[#EAEFEA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#1B4332] mb-4">Why Choose IMR</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#081C15]" data-testid="why-imr-heading">
              The standard your barns deserve
            </h2>
            <p className="mt-4 text-base text-gray-700 leading-relaxed">
              Producers choose IMR for dependable, detail-driven service and a genuine
              commitment to biosecurity — barn after barn, cycle after cycle.
            </p>

            <motion.div
              initial={{ opacity: 0, rotate: -2, y: 20 }}
              whileInView={{ opacity: 1, rotate: 0, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="mt-8 relative max-w-sm"
            >
              <div className="absolute -inset-2.5 bg-[#D4AF37]/20 rounded-2xl rotate-2" aria-hidden="true" />
              <img
                src="/imr-mascot-wash.jpg"
                alt="IMR mascot getting a pressure wash — cleaner barns, happier birds"
                data-testid="why-imr-mascot"
                className="relative rounded-xl w-full object-cover shadow-[0_12px_40px_rgba(27,67,50,0.2)]"
              />
              <p className="absolute -bottom-3 left-4 bg-[#081C15] text-[#D4AF37] font-display font-bold text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-full shadow-lg">
                Cleaner barns, happier birds
              </p>
            </motion.div>

            <div className="mt-10 rounded-xl bg-white border border-[#D8E2DC] p-7" data-testid="who-we-serve-card">
              <h3 className="font-display font-bold text-lg text-[#081C15] mb-5">Who we serve</h3>
              <ul className="space-y-3.5">
                {CUSTOMERS.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <li key={i} className="flex items-center gap-3 text-sm text-gray-700">
                      <Icon size={17} className="text-[#1B4332] shrink-0" />
                      {c.label}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-5 pt-4 border-t border-[#D8E2DC] text-xs text-gray-500 flex items-start gap-2">
                <MapPin size={14} className="text-[#1B4332] shrink-0 mt-0.5" />
                Serving barns across British Columbia, Canada.
              </p>
            </div>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            {REASONS.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.title}
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  data-testid={`why-card-${i}`}
                  className="bg-white rounded-xl border border-[#D8E2DC] p-7 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(27,67,50,0.08)] transition duration-300"
                >
                  <span className="w-12 h-12 rounded-lg bg-[#1B4332] flex items-center justify-center">
                    <Icon size={22} className="text-[#D4AF37]" />
                  </span>
                  <h3 className="mt-5 font-display font-bold text-xl text-[#081C15]">{r.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{r.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
